import React, { useEffect, useReducer, useState } from "react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useDispatch, useSelector } from "react-redux";
import { selectIsLoggedIn, setPopupQuestionDialogShown, setIsShowingPopupQuestionDialog } from "../../redux/authSlice";
import MutipleChoiceQuestion from "../MultipleChoiceQuestion";
import HighChart from "../HighChart";
import { formatPercent } from "../../helpers/utils";
import ProgressDots from "../ProgressDots/ProgressDots";
import _ from 'lodash';
import styles from './PopupQuestionDialog.module.css';
import { Snackbar, Alert } from "@mui/material";


function getChartData(question) {

    // This function is a temporary hack

    // The response for the call to /surveyQuestions/[surveyQuestionId]/results
    // is different than the response for the call to /benchmarking/wizard

    // So, the chartData parameter object we typically pass to the <HighChart> component
    // is different than the question parameter object we pass to the <MultipleChoiceQuestion> component

    // So, this function will generate a chartData object from a question object

    // Ideally, this should be refactored so that both the <HighChart> and <MultipleChoiceQuestion>
    // take a consistently structured parameter

    const chartData = {
        surveyQuestionId: question.surveyQuestionId,
        questionId: question.questionId,
        type: question.surveyQuestionType,
        question: question.shortText,
        fullQuestion: question.text,
        chartType: question.chartType,
        peersOnly: question.peersOnly,
        respondents: question.respondents,
        responses: _.cloneDeep(question.chartData.responses)
    }
    return chartData;
}

const reducer = (state, action) => {

    switch (action.type) {
        case ACTION.NEW_QUESTION:
            const question = action.payload;
            // console.log(question);
            return { ...state, question: question, dataLoaded: true };
        case ACTION.NEW_RESPONSES:
            return {
                ...state,
                question: {
                    ...state.question,
                    responses: action.payload.responses
                }
            };
        default:
            console.log(`unhandled action.type: ${action.type}`)
    }
}

const ACTION = {
    NEW_QUESTION: "newQuestion",
    NEW_RESPONSES: "newResponse",
}

const PopupQuestionDialog = () => {

    // console.log(`PopupQuestionDialog: render at ${new Date().getTime()}`);

    const axiosPrivate = useAxiosPrivate();
    const reduxDispatch = useDispatch();

    const isLoggedIn = useSelector(selectIsLoggedIn);

    const [state, dispatch] = useReducer(reducer, {
        question: null,
        dataLoaded: false
    });

    const [index, setIndex] = useState(0);

    const [alertStatus, setAlertStatus] = useState({
        open: false,
        severity: null,
        message: null,
    });

    useEffect(() => {

        if (!isLoggedIn) {
            return;
        }

        let isMounted = true;
        const controller = new AbortController();

        const getData = async () => {
            try {
                const response = await axiosPrivate.get('/popupQuestions', {
                    signal: controller.signal
                });
                if (isMounted) {
                    dispatch({
                        type: ACTION.NEW_QUESTION, payload: response.data
                    });
                }
            } catch (err) {
                if (isMounted) {
                    console.error(err);
                }
            }
        }

        getData();

        return () => {
            isMounted = false;
            controller.abort();
        }
    }, [isLoggedIn, axiosPrivate])

    const closeDialog = () => {
        reduxDispatch(setPopupQuestionDialogShown());
        reduxDispatch(setIsShowingPopupQuestionDialog(false));
    }

    const saveResponse = async (params) => {

        const json = { ...params };

        try {
            const response = await axiosPrivate.post(`/surveyQuestions/${params.surveyQuestionId}`,
                JSON.stringify(json),
                {
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            console.log(response.data);
            dispatch({
                type: ACTION.NEW_RESPONSES, payload: { ...response.data }
            });
            setAlertStatus(prevState => ({
                ...prevState,
                severity: "success",
                message: "Response saved",
                open: true
            }));
        } catch (err) {
            console.log(err);
        }
    }

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlertStatus(prevState => ({
            ...prevState,
            open: false
        }));
    };

    const screens = 2;

    const screensContainerStyles = {
        transform: `translateX(${formatPercent(-100 * index / 2, 2)}`
    }

    const showPrevQuestion = () => {
        setIndex((prev) => Math.max(0, prev - 1));
    }
    const showNextQuestion = () => {
        setIndex((prev) => Math.min(screens - 1, prev + 1));
    }

    return (
        (state.dataLoaded && isLoggedIn) &&
        <div className="modal dark">
            <div className={styles.wizardDialog}>
                <h2>{state.question.surveyTitle || "Pop-up Question"}</h2>
                <p>{state.question.text}</p>
                <div className={styles.wizardScreensContainer} style={screensContainerStyles}>
                    <div className={styles.wizardScreenContainer}>
                        <MutipleChoiceQuestion question={state.question} answerChoices={state.question.answerChoices} responses={state.question.responses} saveResponse={saveResponse} compact={true} />
                    </div>
                    <div className={styles.wizardScreenContainer}>
                        <HighChart chartData={getChartData(state.question)} chartHeight={400} />
                    </div>
                </div>
                <div className={styles.toolbar}>
                    <button className="link" onClick={showPrevQuestion} disabled={index <= 0}>&lt; Back</button>
                    <ProgressDots totalSteps={screens} currentStep={index + 1} />
                    <button className="link" onClick={showNextQuestion} disabled={index >= (screens - 1)}>Next &gt;</button>
                </div>
                <button className="close" onClick={closeDialog}></button>
                <Snackbar open={alertStatus.open} autoHideDuration={3000} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                    <Alert onClose={handleClose} variant="filled" severity={alertStatus.severity} sx={{ width: '100%' }}>{alertStatus.message}</Alert>
                </Snackbar>
            </div >
        </div>
    )
}

export default PopupQuestionDialog